"use strict";
/*
 * Copyright (C) 2021 JET PROTOCOL HOLDINGS, LLC.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.JetObligation = void 0;
const web3_js_1 = require("@solana/web3.js");
const bn_js_1 = __importDefault(require("bn.js"));
const _1 = require(".");
const __1 = require("..");
const common_1 = require("../common");
/**
 * TODO:
 * @export
 * @class JetObligation
 * @implements {Obligation}
 */
class JetObligation {
    /**
     * Creates an instance of JetObligation.
     * @param {Position[]} positions
     * @param {BN} depositedValue
     * @param {BN} collateralValue
     * @param {BN} loanedValue
     * @param {number} collateralRatio
     * @param {number} utilizationRate
     * @memberof JetObligation
     */
    constructor(positions, depositedValue, collateralValue, loanedValue, collateralRatio, utilizationRate) {
        this.positions = positions;
        this.depositedValue = depositedValue;
        this.collateralValue = collateralValue;
        this.loanedValue = loanedValue;
        this.collateralRatio = collateralRatio;
        this.utilizationRate = utilizationRate;
    }
    /**
     * @static
     * @param {JetClient} client
     * @param {PublicKey} marketAddress
     * @param {JetReserve[]} jetReserves
     * @param {PublicKey} userAddress
     * @returns {(JetObligation | undefined)} JetObligation | undefined
     * @memberof JetObligation
     */
    static use(client, marketAddress, jetReserves, userAddress) {
        return common_1.Hooks.usePromise(async () => client &&
            marketAddress &&
            jetReserves &&
            userAddress &&
            JetObligation.load(client, marketAddress, jetReserves, userAddress), [client, marketAddress, jetReserves, userAddress]);
    }
    /**
     *
     * @param {JetClient} client
     * @param {PublicKey} marketAddress
     * @param {JetReserve[]} jetReserves
     * @param {PublicKey} userAddress
     * @returns {Promise<JetObligation>}
     */
    static async load(client, marketAddress, jetReserves, userAddress) {
        const market = await _1.JetMarket.load(client, marketAddress);
        const [user, reserves] = await Promise.all([
            _1.JetUser.load(client, market, jetReserves, userAddress),
            _1.JetReserve.loadMultiple(client, market)
        ]);
        return this.create(market, user, reserves.map(reserve => reserve.data));
    }
    /**
     * TODO:
     * @static
     * @param {JetMarketData} market
     * @param {JetUserData} user
     * @param {ReserveData[]} reserveData
     * @param {number[]} prices
     * @returns
     * @memberof JetObligation
     */
    static create(market, user, reserveData) {
        var _a, _b, _c;
        const deposits = user.deposits();
        const collateral = user.collateral();
        const loans = user.loans();
        const balances = [];
        // Sum of Deposited and borrowed
        let depositedValue = 0;
        let collateralValue = 0;
        let loanedValue = 0;
        // Token balances
        for (let i = 0; i < market.reserves.length; i++) {
            const reserveCache = market.reserves[i];
            const reserve = reserveData[i];
            if (reserveCache.reserve.equals(web3_js_1.PublicKey.default)) {
                continue;
            }
            if (!reserveCache.reserve.equals(reserve.address)) {
                throw new Error("market reserves do not match reserve list.");
            }
            const depositNotes = deposits.find(deposit => deposit.mint.equals(reserve.depositNoteMint));
            const collateralNotes = collateral.find(collateral => collateral.mint.equals(reserve.depositNoteMint));
            const loanNotes = loans.find(loan => loan.mint.equals(reserve.loanNoteMint));
            const balance = {
                reserve: reserve,
                depositNotes,
                depositBalance: (_a = depositNotes === null || depositNotes === void 0 ? void 0 : depositNotes.mulb(reserveCache.depositNoteExchangeRate).divb(new bn_js_1.default(1e15))) !== null && _a !== void 0 ? _a : __1.TokenAmount.zero(0, reserve.depositNoteMint),
                collateralNotes: collateralNotes,
                collateralBalance: (_b = collateralNotes === null || collateralNotes === void 0 ? void 0 : collateralNotes.mulb(reserveCache.depositNoteExchangeRate).divb(new bn_js_1.default(1e15))) !== null && _b !== void 0 ? _b : __1.TokenAmount.zero(0, reserve.depositNoteMint),
                loanNotes: loanNotes,
                loanBalance: (_c = loanNotes === null || loanNotes === void 0 ? void 0 : loanNotes.mulb(reserveCache.loanNoteExchangeRate).divb(new bn_js_1.default(1e15))) !== null && _c !== void 0 ? _c : __1.TokenAmount.zero(0, reserve.loanNoteMint),
                //todo fixme
                maxDepositAmount: undefined,
                maxWithdrawAmount: undefined,
                maxBorrowAmount: undefined,
                maxRepayAmount: undefined
            };
            const price = reserve.priceData.price;
            if (price != undefined) {
                if (balance.depositBalance) {
                    depositedValue += balance.depositBalance.muln(price).tokens;
                }
                if (balance.collateralBalance) {
                    collateralValue += balance.collateralBalance.muln(price).tokens;
                }
                if (balance.loanBalance) {
                    loanedValue += balance.loanBalance.muln(price).tokens;
                }
            }
            balances[i] = balance;
        }
        // calculate collateral ratio
        const collateralRatio = loanedValue === 0 ? 0 : collateralValue / loanedValue;
        //calculate utilization ratio
        const utilizationRate = collateralValue === 0 ? 0 : loanedValue / collateralValue;
        return new JetObligation(balances, depositedValue, collateralValue, loanedValue, collateralRatio, utilizationRate);
    }
}
exports.JetObligation = JetObligation;
//# sourceMappingURL=obligation.js.map